import { useState } from 'react';
import type { AnchorSide, BpmnFlowType, Cardinality, ConnectorEndpoint, DiagramEdge, DiagramNode, EntityField, NodeStyle, UmlRelationshipType } from '../core/types';

interface Props {
  readOnly?: boolean;
  node: DiagramNode | null;
  edge: DiagramEdge | null;
  selectedEdges: DiagramEdge[];
  selectedEdgeCount: number;
  isBpmnEdge?: boolean;
  documentTitle: string;
  pageName: string;
  currentPageId: string;
  pages: Array<{ id: string; name: string }>;
  selectionCount: number;
  selectedNodes: DiagramNode[];
  pageNodes: DiagramNode[];
  onDocumentTitle: (title: string) => void;
  onPageName: (name: string) => void;
  onNavigatePage: (pageId: string) => void;
  onPatchNode: (patch: Partial<DiagramNode>) => void;
  onPatchEdge: (patch: Partial<DiagramEdge>) => void;
  onPatchSelectedEdges: (patch: Partial<DiagramEdge>) => void;
  onReverseEdge?: () => void;
  onPatchSelectionStyle: (patch: Partial<NodeStyle>) => void;
  onPatchSelectedNodes: (patch: Partial<DiagramNode>) => void;
  onAddOrgChild: (parentId: string) => void;
  onArrangeOrg: () => void;
  onAddMindChild: (parentId: string) => void;
  onArrangeMind: () => void;
  onNormalizeBpmn?: () => void;
  onSaveToLibrary?: (node: DiagramNode) => void;
}




const QUICK_FILLS = ['#ffffff','#fff3e8','#fff7d6','#ecfdf3','#eff8ff','#eef4ff','#f4f3ff','#fdf2fa','#f2f4f7','#071d3d'];

function StyleFields({ style, disabled = false, onPatch }: { style: NodeStyle; disabled?: boolean; onPatch: (patch: Partial<NodeStyle>) => void }) {
  return (
    <div className="style-fields">
      <div className="subsection-title">Aparência</div>
      <div className="field-grid three">
        <label>Preenchimento<input type="color" value={style.fill} disabled={disabled} onChange={(event) => onPatch({ fill: event.target.value })} /></label>
        <label>Borda<input type="color" value={style.stroke} disabled={disabled} onChange={(event) => onPatch({ stroke: event.target.value })} /></label>
        <label>Texto<input type="color" value={style.textColor} disabled={disabled} onChange={(event) => onPatch({ textColor: event.target.value })} /></label>
      </div>
      <div className="fill-preset-block">
        <span>Preenchimento rápido</span>
        <div className="fill-preset-row" role="group" aria-label="Cores rápidas de preenchimento">
          {QUICK_FILLS.map((color) => <button key={color} type="button" disabled={disabled} className={style.fill.toLowerCase() === color ? 'active' : ''} style={{ background: color }} title={color === '#071d3d' ? 'Azul OWDEX' : color} aria-label={`Preenchimento ${color}`} onClick={() => onPatch({ fill: color })} />)}
        </div>
      </div>
      <div className="field-grid two">
        <label>Espessura da borda
          <input type="number" min="0.5" max="20" step="0.5" value={style.strokeWidth} disabled={disabled} onChange={(event) => onPatch({ strokeWidth: Math.min(20, Math.max(0.5, Number(event.target.value) || 0.5)) })} />
        </label>
        <label>Padrão da borda
          <select value={style.linePattern} disabled={disabled} onChange={(event) => onPatch({ linePattern: event.target.value as NodeStyle['linePattern'] })}>
            <option value="solid">Contínua</option>
            <option value="dashed">Tracejada</option>
          </select>
        </label>
      </div>
      <div className="field-grid two">
        <label>Tamanho do texto
          <input type="number" min="6" max="160" value={style.fontSize} disabled={disabled} onChange={(event) => onPatch({ fontSize: Math.min(160, Math.max(6, Number(event.target.value) || 6)) })} />
        </label>
        <label>Peso do texto
          <select value={style.fontWeight} disabled={disabled} onChange={(event) => onPatch({ fontWeight: Number(event.target.value) })}>
            <option value="400">Normal</option>
            <option value="550">Médio</option>
            <option value="700">Negrito</option>
            <option value="800">Extra forte</option>
          </select>
        </label>
      </div>
      <label>Alinhamento do texto
        <select value={style.textAlign} disabled={disabled} onChange={(event) => onPatch({ textAlign: event.target.value as NodeStyle['textAlign'] })}>
          <option value="left">Esquerda</option>
          <option value="center">Centro</option>
          <option value="right">Direita</option>
        </select>
      </label>
      <label>Opacidade
        <div className="rotation-field">
          <input type="range" min="5" max="100" step="1" value={Math.round(style.opacity * 100)} disabled={disabled} onChange={(event) => onPatch({ opacity: Math.min(1, Math.max(0.05, Number(event.target.value) / 100)) })} />
          <input type="number" min="5" max="100" step="1" value={Math.round(style.opacity * 100)} disabled={disabled} onChange={(event) => onPatch({ opacity: Math.min(1, Math.max(0.05, (Number(event.target.value) || 5) / 100)) })} />
          <span>%</span>
        </div>
      </label>
      <label>Arredondamento
        <input type="range" min="0" max="80" step="1" value={style.radius} disabled={disabled} onChange={(event) => onPatch({ radius: Number(event.target.value) || 0 })} />
      </label>
    </div>
  );
}

function DataFields({ node, onPatchNode }: { node: DiagramNode; onPatchNode: (patch: Partial<DiagramNode>) => void }) {
  const [keyName, setKeyName] = useState('');
  const [value, setValue] = useState('');
  const entries = Object.entries(node.data ?? {});

  function updateData(key: string, nextValue: string) {
    if (['__proto__','prototype','constructor'].includes(key)) return;
    onPatchNode({ data: { ...node.data, [key]: nextValue.slice(0, 4000) } });
  }

  function removeData(key: string) {
    const next = { ...node.data };
    delete next[key];
    onPatchNode({ data: next });
  }

  function addData() {
    const cleanKey = keyName.trim().slice(0, 80);
    if (!cleanKey || node.locked || ['__proto__','prototype','constructor'].includes(cleanKey)) return;
    onPatchNode({ data: { ...node.data, [cleanKey]: value.slice(0, 4000) } });
    setKeyName('');
    setValue('');
  }

  return (
    <div className="data-section">
      <div className="subsection-title">Dados do objeto</div>
      {entries.map(([key, current]) => (
        <div className="data-row" key={key}>
          <span title={key}>{key}</span>
          <input disabled={node.locked} value={String(current ?? '')} onChange={(event) => updateData(key, event.target.value)} />
          <button type="button" disabled={node.locked} onClick={() => removeData(key)} aria-label={`Remover ${key}`}>×</button>
        </div>
      ))}
      {entries.length === 0 && <div className="data-empty">Nenhum dado adicional.</div>}
      <div className="data-add">
        <input disabled={node.locked} value={keyName} onChange={(event) => setKeyName(event.target.value)} placeholder="Campo" />
        <input disabled={node.locked} value={value} onChange={(event) => setValue(event.target.value)} placeholder="Valor" onKeyDown={(event) => { if (event.key === 'Enter') addData(); }} />
        <button type="button" disabled={node.locked || !keyName.trim()} onClick={addData}>Adicionar</button>
      </div>
    </div>
  );
}

const anchors: Array<{ value: AnchorSide; label: string }> = [
  { value: 'auto', label: 'Automática' },
  { value: 'topLeft', label: 'Cima · esquerda' },
  { value: 'top', label: 'Cima · centro' },
  { value: 'topRight', label: 'Cima · direita' },
  { value: 'rightTop', label: 'Direita · cima' },
  { value: 'right', label: 'Direita · centro' },
  { value: 'rightBottom', label: 'Direita · baixo' },
  { value: 'bottomRight', label: 'Baixo · direita' },
  { value: 'bottom', label: 'Baixo · centro' },
  { value: 'bottomLeft', label: 'Baixo · esquerda' },
  { value: 'leftBottom', label: 'Esquerda · baixo' },
  { value: 'left', label: 'Esquerda · centro' },
  { value: 'leftTop', label: 'Esquerda · cima' },
];

const endpointOptions: Array<{ value: ConnectorEndpoint; label: string }> = [
  { value: 'none', label: 'Reta' },
  { value: 'arrow', label: 'Seta' },
  { value: 'circle', label: 'Círculo' },
  { value: 'diamond', label: 'Losango' },
  { value: 'square', label: 'Quadrado' },
];

function EndpointGlyph({ type, reverse = false }: { type: ConnectorEndpoint; reverse?: boolean }) {
  const start = reverse ? 19 : 3;
  const end = reverse ? 3 : 19;
  return <svg viewBox="0 0 22 14" aria-hidden="true"><line x1={start} y1="7" x2={end} y2="7" />{type === 'arrow' && <path d={reverse ? 'M 7 3 L 2 7 L 7 11' : 'M 15 3 L 20 7 L 15 11'} />}{type === 'circle' && <circle cx={end} cy="7" r="3" />}{type === 'diamond' && <path d={`M ${end} 3 L ${end + (reverse ? -4 : 4)} 7 L ${end} 11 L ${end + (reverse ? 4 : -4)} 7 Z`} />}{type === 'square' && <rect x={end - 3} y="4" width="6" height="6" rx="1" />}</svg>;
}

function EndpointPicker({ label, value, reverse = false, onChange }: { label: string; value: ConnectorEndpoint; reverse?: boolean; onChange: (value: ConnectorEndpoint) => void }) {
  return <div className="endpoint-picker"><span className="endpoint-picker-label">{label}</span><div className="endpoint-options" role="group" aria-label={label}>{endpointOptions.map((option) => <button key={option.value} type="button" className={value === option.value ? 'active' : ''} title={option.label} aria-pressed={value === option.value} onClick={() => onChange(option.value)}><EndpointGlyph type={option.value} reverse={reverse} /><span>{option.label}</span></button>)}</div></div>;
}

export function PropertiesPanel({ readOnly = false, node, edge, selectedEdges, selectedEdgeCount, isBpmnEdge = false, documentTitle, pageName, currentPageId, pages, selectionCount, selectedNodes, pageNodes, onDocumentTitle, onPageName, onNavigatePage, onPatchNode, onPatchEdge, onPatchSelectedEdges, onReverseEdge, onPatchSelectionStyle, onPatchSelectedNodes, onAddOrgChild, onArrangeOrg, onAddMindChild, onArrangeMind, onNormalizeBpmn, onSaveToLibrary }: Props) {
  const contextLabel = selectedEdgeCount > 1 ? `${selectedEdgeCount} conexões selecionadas` : node ? 'Objeto selecionado' : edge ? 'Conexão selecionada' : selectionCount > 1 ? `${selectionCount} objetos selecionados` : 'Documento';
  const sampleEdge = selectedEdges[0] ?? null;

  return (
    <aside id="properties-panel" className={`right-panel ${readOnly ? 'read-only' : ''}`} aria-readonly={readOnly} tabIndex={-1} aria-label="Propriedades do diagrama">
      <div className="panel-heading">
        <strong>Propriedades</strong>
        <span>{contextLabel}</span>
      </div>
      {readOnly && <div className="properties-readonly">Somente leitura — você pode navegar, buscar, exportar e comentar, mas não alterar este projeto.</div>}

      {selectedEdgeCount > 1 && sampleEdge ? (
        <div className="property-section bulk-edge-properties">
          <div className="info-card bulk-edit-banner">
            <strong>Edição em massa de conexões</strong>
            <p>{selectedEdgeCount} traçados selecionados. Cada alteração abaixo é aplicada a todos de uma só vez.</p>
          </div>
          <label>Traçado
            <select value={sampleEdge.style} disabled={readOnly} onChange={(event) => onPatchSelectedEdges({ style: event.target.value as DiagramEdge['style'] })}>
              <option value="orthogonal">Ortogonal inteligente</option>
              <option value="straight">Reto</option>
            </select>
          </label>
          <label>Roteamento
            <select value={sampleEdge.routingMode ?? 'auto'} disabled={readOnly} onChange={(event) => onPatchSelectedEdges({ routingMode: event.target.value as DiagramEdge['routingMode'], ...(event.target.value === 'auto' ? { waypoints: [] } : {}) })}>
              <option value="auto">Automático</option>
              <option value="manual">Manual por pontos</option>
            </select>
          </label>
          <label>Padrão da linha
            <select value={sampleEdge.linePattern ?? 'solid'} disabled={readOnly} onChange={(event) => onPatchSelectedEdges({ linePattern: event.target.value as DiagramEdge['linePattern'] })}>
              <option value="solid">Contínua</option>
              <option value="dashed">Tracejada</option>
            </select>
          </label>
          <div className="field-grid two">
            <label>Cor
              <input type="color" value={sampleEdge.stroke} disabled={readOnly} onChange={(event) => onPatchSelectedEdges({ stroke: event.target.value })} />
            </label>
            <label>Espessura
              <input type="number" min="1" max="8" step="0.5" value={sampleEdge.strokeWidth} disabled={readOnly} onChange={(event) => onPatchSelectedEdges({ strokeWidth: Math.min(8, Math.max(1, Number(event.target.value) || 1)) })} />
            </label>
          </div>
          <div className="connector-endpoints-card">
            <div className="subsection-title">Pontas em massa</div>
            <p className="muted-copy">A opção escolhida substitui a ponta correspondente em todas as conexões selecionadas.</p>
            <EndpointPicker label="Origem" reverse value={sampleEdge.startMarker ?? (sampleEdge.arrowStart ? 'arrow' : 'none')} onChange={(value) => onPatchSelectedEdges({ startMarker: value, arrowStart: value === 'arrow' })} />
            <EndpointPicker label="Destino" value={sampleEdge.endMarker ?? (sampleEdge.arrowEnd ? 'arrow' : 'none')} onChange={(value) => onPatchSelectedEdges({ endMarker: value, arrowEnd: value === 'arrow' })} />
          </div>
          <button type="button" className="inline-action" disabled={readOnly} onClick={() => onPatchSelectedEdges({ routingMode: 'auto', waypoints: [], bundleId: null })}>Separar e recalcular rotas</button>
          <small className="muted-copy">Dica: use Shift + clique para selecionar linhas individuais ou arraste uma seleção sobre os traçados.</small>
        </div>
      ) : !node && !edge ? (
        <div className="property-section">
          {selectionCount > 1 ? (
            <>
              <div className="info-card">
                <strong>Seleção múltipla</strong>
                <p>As alterações abaixo são aplicadas de uma vez aos objetos selecionados que não estiverem travados.</p>
              </div>
              {selectedNodes[0] && <>
                <div className="subsection-title">Geometria em massa</div>
                <div className="field-grid two">
                  <label>Largura<input type="number" min="24" disabled={readOnly} value={Math.round(selectedNodes[0].width)} onChange={(event) => onPatchSelectedNodes({ width: Math.max(24, Number(event.target.value) || 24) })} /></label>
                  <label>Altura<input type="number" min="24" disabled={readOnly} value={Math.round(selectedNodes[0].height)} onChange={(event) => onPatchSelectedNodes({ height: Math.max(24, Number(event.target.value) || 24) })} /></label>
                </div>
                <label>Rotação de todos
                  <input type="number" min="-180" max="180" disabled={readOnly} value={Math.round(selectedNodes[0].rotation)} onChange={(event) => onPatchSelectedNodes({ rotation: Math.max(-180, Math.min(180, Number(event.target.value) || 0)) })} />
                </label>
                <StyleFields style={selectedNodes[0].style} onPatch={onPatchSelectionStyle} />
              </>}
            </>
          ) : (
            <>
              <label>Título do diagrama
                <input value={documentTitle} onChange={(event) => onDocumentTitle(event.target.value)} />
              </label>
              <label>Página atual
                <input value={pageName} onChange={(event) => onPageName(event.target.value)} />
              </label>
              <div className="info-card">
                <strong>Salvamento automático</strong>
                <p>As alterações são persistidas localmente no navegador e ficam preparadas para sincronização com o servidor.</p>
              </div>
              <div className="info-card ai-card">
                <strong>OWDEX IA</strong>
                <p>Gateway preparado. A integração permanece desabilitada até a etapa específica de IA.</p>
              </div>
            </>
          )}
        </div>
      ) : edge ? (
        <div className="property-section">
          <label>Rótulo da conexão
            <input value={edge.label} onChange={(event) => onPatchEdge({ label: event.target.value })} placeholder="Ex.: Sim, Não, Aprovação" />
          </label>
          <label>Traçado
            <select value={edge.style} onChange={(event) => onPatchEdge({ style: event.target.value as DiagramEdge['style'] })}>
              <option value="orthogonal">Ortogonal inteligente</option>
              <option value="straight">Reto</option>
            </select>
          </label>
          <label>Roteamento
            <select value={edge.routingMode ?? 'auto'} onChange={(event) => onPatchEdge({ routingMode: event.target.value as DiagramEdge['routingMode'], waypoints: event.target.value === 'auto' ? [] : edge.waypoints ?? [] })}>
              <option value="auto">Automático</option>
              <option value="manual">Manual por pontos</option>
            </select>
          </label>
          <div className="info-card">
            <strong>{edge.routingMode === 'manual' ? `${edge.waypoints?.length ?? 0} ponto(s) manual(is)` : 'Rota automática'}</strong>
            <p>{edge.routingMode === 'manual' ? 'Arraste os pontos azuis no canvas. Duplo clique em um ponto remove. Alt + duplo clique na linha adiciona.' : 'O OWDEX Draw calcula a rota e tenta desviar dos objetos automaticamente.'}</p>
            {edge.routingMode === 'manual' && (edge.waypoints?.length ?? 0) > 0 && <button type="button" className="inline-action" onClick={() => onPatchEdge({ routingMode: 'auto', waypoints: [] })}>Voltar ao automático</button>}
          </div>
          <div className="field-grid two">
            <label>Origem
              <select value={edge.sourceAnchor ?? 'auto'} onChange={(event) => onPatchEdge({ sourceAnchor: event.target.value as AnchorSide })}>
                {anchors.map((anchor) => <option key={anchor.value} value={anchor.value}>{anchor.label}</option>)}
              </select>
            </label>
            <label>Destino
              <select value={edge.targetAnchor ?? 'auto'} onChange={(event) => onPatchEdge({ targetAnchor: event.target.value as AnchorSide })}>
                {anchors.map((anchor) => <option key={anchor.value} value={anchor.value}>{anchor.label}</option>)}
              </select>
            </label>
          </div>
          <div className="field-grid two">
            <label>Cardinalidade origem
              <select value={edge.sourceCardinality ?? ''} onChange={(event) => onPatchEdge({ sourceCardinality: (event.target.value || undefined) as Cardinality | undefined })}>
                <option value="">Nenhuma</option><option value="1">1</option><option value="0..1">0..1</option><option value="N">N</option><option value="1..N">1..N</option><option value="0..N">0..N</option>
              </select>
            </label>
            <label>Cardinalidade destino
              <select value={edge.targetCardinality ?? ''} onChange={(event) => onPatchEdge({ targetCardinality: (event.target.value || undefined) as Cardinality | undefined })}>
                <option value="">Nenhuma</option><option value="1">1</option><option value="0..1">0..1</option><option value="N">N</option><option value="1..N">1..N</option><option value="0..N">0..N</option>
              </select>
            </label>
          </div>
          <label>Padrão da linha
            <select value={edge.linePattern ?? 'solid'} onChange={(event) => onPatchEdge({ linePattern: event.target.value as DiagramEdge['linePattern'] })}>
              <option value="solid">Contínua</option>
              <option value="dashed">Tracejada</option>
            </select>
          </label>
          {isBpmnEdge && <label>Fluxo BPMN
            <select value={edge.bpmnFlowType ?? 'sequence'} onChange={(event) => { const flow = event.target.value as BpmnFlowType; onPatchEdge({ bpmnFlowType: flow, linePattern: flow === 'message' || flow === 'association' ? 'dashed' : 'solid', arrowEnd: flow !== 'association', endMarker: flow === 'association' ? 'none' : 'arrow' }); }}>
              <option value="sequence">Sequência</option>
              <option value="message">Mensagem</option>
              <option value="association">Associação</option>
            </select>
          </label>}
          <label>Relacionamento UML
            <select value={edge.relationshipType ?? ''} onChange={(event) => onPatchEdge({ relationshipType: (event.target.value || undefined) as UmlRelationshipType | undefined })}>
              <option value="">Nenhum / genérico</option>
              <option value="association">Associação</option>
              <option value="dependency">Dependência</option>
              <option value="inheritance">Herança / generalização</option>
              <option value="aggregation">Agregação</option>
              <option value="composition">Composição</option>
              <option value="include">«include»</option>
              <option value="extend">«extend»</option>
            </select>
          </label>
          <div className="field-grid two">
            <label>Cor
              <input type="color" value={edge.stroke} onChange={(event) => onPatchEdge({ stroke: event.target.value })} />
            </label>
            <label>Espessura
              <input type="number" min="1" max="8" step="0.5" value={edge.strokeWidth} onChange={(event) => onPatchEdge({ strokeWidth: Math.min(8, Math.max(1, Number(event.target.value) || 1)) })} />
            </label>
          </div>
          <div className="connector-direction-actions">
            <button type="button" className="inline-action" disabled={readOnly} onClick={onReverseEdge}>Inverter direção</button>
            <small>Troca origem e destino, incluindo âncoras, pontas e cardinalidades.</small>
          </div>
          <div className="connector-endpoints-card">
            <div className="subsection-title">Pontas da conexão</div>
            <p className="muted-copy">Escolha visualmente a ponta de cada lado. “Reta” remove o marcador.</p>
            <EndpointPicker label="Origem" reverse value={edge.startMarker ?? (edge.arrowStart ? 'arrow' : 'none')} onChange={(value) => onPatchEdge({ startMarker: value, arrowStart: value === 'arrow' })} />
            <EndpointPicker label="Destino" value={edge.endMarker ?? (edge.arrowEnd ? 'arrow' : 'none')} onChange={(value) => onPatchEdge({ endMarker: value, arrowEnd: value === 'arrow' })} />
            {(edge.relationshipType || edge.bpmnFlowType === 'association') && <small className="semantic-marker-note">Relacionamentos UML/BPMN podem substituir visualmente estas pontas conforme a notação.</small>}
          </div>
        </div>
      ) : node ? (
        <div className="property-section">
          <button type="button" className="inline-action" disabled={readOnly} onClick={() => onSaveToLibrary?.(node)}>Salvar na minha biblioteca</button>
          <div className="property-edit-hint"><strong>Título do objeto</strong><span>Duplo clique no objeto também edita diretamente no canvas.</span></div>
          <label>Texto / título
            <textarea rows={3} value={node.text} disabled={node.locked} onChange={(event) => onPatchNode({ text: event.target.value })} />
          </label>
          <div className="field-grid two">
            <label>X<input type="number" value={Math.round(node.x)} disabled={node.locked} onChange={(event) => onPatchNode({ x: Number(event.target.value) || 0 })} /></label>
            <label>Y<input type="number" value={Math.round(node.y)} disabled={node.locked} onChange={(event) => onPatchNode({ y: Number(event.target.value) || 0 })} /></label>
          </div>
          <div className="field-grid two">
            <label>Largura<input type="number" min="40" value={Math.round(node.width)} disabled={node.locked} onChange={(event) => onPatchNode({ width: Math.max(40, Number(event.target.value) || 40) })} /></label>
            <label>Altura<input type="number" min="30" value={Math.round(node.height)} disabled={node.locked} onChange={(event) => onPatchNode({ height: Math.max(30, Number(event.target.value) || 30) })} /></label>
          </div>
          <label>Rotação
            <div className="rotation-field">
              <input type="range" min="-180" max="180" step="1" value={Math.round(node.rotation)} disabled={node.locked} onChange={(event) => onPatchNode({ rotation: Number(event.target.value) || 0 })} />
              <input type="number" min="-180" max="180" value={Math.round(node.rotation)} disabled={node.locked} onChange={(event) => onPatchNode({ rotation: Math.max(-180, Math.min(180, Number(event.target.value) || 0)) })} />
              <span>°</span>
            </div>
          </label>
          <div className="specialized-fields">
            <div className="subsection-title">Navegação interna</div>
            <label>Ao clicar no atalho do objeto
              <select
                disabled={node.locked}
                value={typeof node.data.linkPageId === 'string' ? node.data.linkPageId : ''}
                onChange={(event) => onPatchNode({ data: { ...node.data, linkPageId: event.target.value || null } })}
              >
                <option value="">Nenhuma página</option>
                {pages.filter((item) => item.id !== currentPageId).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>
            {typeof node.data.linkPageId === 'string' && pages.some((item) => item.id === node.data.linkPageId) && (
              <button type="button" className="inline-action" onClick={() => onNavigatePage(String(node.data.linkPageId))}>Abrir página vinculada</button>
            )}
          </div>
          {['bpmnTask','bpmnSubprocess','bpmnDataObject','bpmnEvent','bpmnBoundaryEvent','bpmnTextAnnotation','bpmnGateway','bpmnPool','bpmnLane'].includes(node.type) && <div className="specialized-fields"><div className="subsection-title">Swimlanes BPMN</div><button type="button" className="inline-action" disabled={node.locked} onClick={onNormalizeBpmn}>Sincronizar pool/raia pela posição</button></div>}
          {node.type === 'bpmnTask' && (
            <label>Tipo da tarefa BPMN
              <select disabled={node.locked} value={String(node.data.taskType ?? 'user')} onChange={(event) => onPatchNode({ data: { ...node.data, taskType: event.target.value } })}>
                <option value="user">Usuário</option>
                <option value="service">Serviço</option>
                <option value="manual">Manual</option>
                <option value="script">Script</option>
                <option value="businessRule">Regra de negócio</option>
                <option value="send">Envio</option>
                <option value="receive">Recebimento</option>
              </select>
            </label>
          )}
          {node.type === 'bpmnSubprocess' && (
            <div className="specialized-fields"><div className="subsection-title">Subprocesso BPMN</div>
              <label>Tipo<select disabled={node.locked} value={String(node.data.subprocessType ?? 'embedded')} onChange={(event) => onPatchNode({ data: { ...node.data, subprocessType: event.target.value } })}><option value="embedded">Embutido</option><option value="event">Evento</option><option value="call">Atividade chamada</option></select></label>
              <label className="checkbox-row"><input type="checkbox" disabled={node.locked} checked={node.data.expanded !== false} onChange={(event) => onPatchNode({ data: { ...node.data, expanded: event.target.checked } })} /> Expandido</label>
            </div>
          )}
          {node.type === 'bpmnDataObject' && <label>Estado / observação do dado<input disabled={node.locked} value={String(node.data.dataState ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, dataState: event.target.value } })} placeholder="Ex.: aprovado, validado..." /></label>}
          {node.type === 'bpmnEvent' && (
            <div className="specialized-fields">
              <label>Tipo do evento BPMN
                <select disabled={node.locked} value={String(node.data.eventType ?? 'start')} onChange={(event) => onPatchNode({ data: { ...node.data, eventType: event.target.value } })}>
                  <option value="start">Início</option>
                  <option value="intermediate">Intermediário</option>
                  <option value="end">Fim</option>
                </select>
              </label>
              <label>Definição do evento
                <select disabled={node.locked} value={String(node.data.eventDefinition ?? 'none')} onChange={(event) => onPatchNode({ data: { ...node.data, eventDefinition: event.target.value } })}>
                  <option value="none">Nenhuma</option>
                  <option value="message">Mensagem</option>
                  <option value="timer">Temporizador</option>
                  <option value="error">Erro</option>
                  <option value="signal">Sinal</option>
                  <option value="conditional">Condicional</option>
                  <option value="link">Link</option>
                  <option value="terminate">Terminação</option>
                </select>
              </label>
            </div>
          )}
          {node.type === 'bpmnBoundaryEvent' && (
            <div className="specialized-fields">
              <div className="subsection-title">Evento de borda BPMN</div>
              <label>Definição
                <select disabled={node.locked} value={String(node.data.eventDefinition ?? 'timer')} onChange={(event) => onPatchNode({ data: { ...node.data, eventDefinition: event.target.value } })}>
                  <option value="timer">Temporizador</option><option value="message">Mensagem</option><option value="error">Erro</option><option value="signal">Sinal</option><option value="conditional">Condicional</option><option value="cancel">Cancelamento</option><option value="compensation">Compensação</option>
                </select>
              </label>
              <label>Atividade anexada
                <select disabled={node.locked} value={typeof node.data.attachedToId === 'string' ? node.data.attachedToId : ''} onChange={(event) => onPatchNode({ data: { ...node.data, attachedToId: event.target.value || null } })}>
                  <option value="">Selecione…</option>
                  {pageNodes.filter((item) => ['bpmnTask','bpmnSubprocess'].includes(item.type) && item.id !== node.id).map((item) => <option key={item.id} value={item.id}>{item.text || item.type}</option>)}
                </select>
              </label>
              <label className="checkbox-row"><input type="checkbox" disabled={node.locked} checked={node.data.cancelActivity !== false} onChange={(event) => onPatchNode({ data: { ...node.data, cancelActivity: event.target.checked } })} /> Interrompe a atividade</label>
            </div>
          )}
          {node.type === 'bpmnTextAnnotation' && <div className="specialized-fields"><div className="subsection-title">Anotação BPMN</div><p className="muted-copy">Conecte por uma associação tracejada ao elemento documentado.</p></div>}
          {node.type === 'bpmnGateway' && (
            <label>Tipo do gateway BPMN
              <select disabled={node.locked} value={String(node.data.gatewayType ?? 'exclusive')} onChange={(event) => onPatchNode({ data: { ...node.data, gatewayType: event.target.value } })}>
                <option value="exclusive">Exclusivo (X)</option>
                <option value="parallel">Paralelo (+)</option>
                <option value="inclusive">Inclusivo (O)</option>
                <option value="eventBased">Baseado em eventos</option>
                <option value="complex">Complexo</option>
              </select>
            </label>
          )}
          {node.type === 'bpmnPool' && (
            <label>Participante
              <input disabled={node.locked} value={String(node.data.participant ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, participant: event.target.value } })} placeholder="Empresa, cliente, sistema..." />
            </label>
          )}
          {node.type === 'bpmnLane' && (
            <label>Responsável pela raia
              <input disabled={node.locked} value={String(node.data.responsible ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, responsible: event.target.value } })} placeholder="Área ou responsável" />
            </label>
          )}
          {node.type === 'orgPerson' && (
            <div className="specialized-fields">
              <div className="subsection-title">Dados do organograma</div>
              <label>Nome<input disabled={node.locked} value={String(node.data.name ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, name: event.target.value } })} /></label>
              <label>Cargo<input disabled={node.locked} value={String(node.data.role ?? node.data.cargo ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, role: event.target.value } })} /></label>
              <label>Departamento<input disabled={node.locked} value={String(node.data.department ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, department: event.target.value } })} /></label>
            </div>
          )}
          {node.type === 'orgDepartment' && (
            <label>Nome do departamento
              <input disabled={node.locked} value={String(node.data.department ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, department: event.target.value } })} />
            </label>
          )}
          {node.type === 'umlClass' && (
            <div className="specialized-fields uml-list-editor">
              <div className="subsection-title">Estrutura da classe UML</div>
              <label>Atributos (um por linha)
                <textarea disabled={node.locked} value={(node.umlAttributes ?? []).join('\n')} onChange={(event) => onPatchNode({ umlAttributes: event.target.value.split(/\r?\n/).slice(0, 500) })} placeholder={'+ id: UUID\n- nome: string'} />
              </label>
              <label>Métodos (um por linha)
                <textarea disabled={node.locked} value={(node.umlMethods ?? []).join('\n')} onChange={(event) => onPatchNode({ umlMethods: event.target.value.split(/\r?\n/).slice(0, 500) })} placeholder="+ salvar(): void" />
              </label>
            </div>
          )}
          {['networkServer', 'networkSwitch', 'networkRouter', 'networkFirewall', 'networkClient', 'networkCloud', 'networkLoadBalancer', 'networkDatabase', 'networkStorage'].includes(node.type) && (
            <div className="specialized-fields network-fields-editor">
              <div className="subsection-title">Dados de rede</div>
              <label>Hostname / identificação<input disabled={node.locked} value={String(node.data.hostname ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, hostname: event.target.value } })} placeholder="Ex.: SRV-APP-01" /></label>
              <div className="field-grid two">
                <label>IP<input disabled={node.locked} value={String(node.data.ip ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, ip: event.target.value } })} placeholder="192.168.1.10" /></label>
                <label>VLAN<input disabled={node.locked} value={String(node.data.vlan ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, vlan: event.target.value } })} placeholder="10" /></label>
              </div>
              <label>Status
                <select disabled={node.locked} value={String(node.data.status ?? 'unknown')} onChange={(event) => onPatchNode({ data: { ...node.data, status: event.target.value } })}>
                  <option value="unknown">Não informado</option>
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                  <option value="maintenance">Manutenção</option>
                  <option value="planned">Planejado</option>
                </select>
              </label>
              <label>Localização<input disabled={node.locked} value={String(node.data.location ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, location: event.target.value } })} placeholder="Rack, sala, filial..." /></label>
            </div>
          )}
          {['cloudAws','cloudAzure','cloudGcp','cloudService'].includes(node.type) && (
            <div className="specialized-fields cloud-fields-editor">
              <div className="subsection-title">Dados Cloud</div>
              <label>Provedor<input disabled={node.locked} value={String(node.data.provider ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, provider: event.target.value } })} placeholder="AWS, Azure, Google Cloud..." /></label>
              <label>Serviço / recurso<input disabled={node.locked} value={String(node.data.service ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, service: event.target.value } })} placeholder="Ex.: EC2, App Service, Cloud Run" /></label>
              <div className="field-grid two">
                <label>Região<input disabled={node.locked} value={String(node.data.region ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, region: event.target.value } })} placeholder="sa-east-1" /></label>
                <label>Ambiente<input disabled={node.locked} value={String(node.data.environment ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, environment: event.target.value } })} placeholder="Produção" /></label>
              </div>
              <label>Conta / projeto<input disabled={node.locked} value={String(node.data.account ?? '')} onChange={(event) => onPatchNode({ data: { ...node.data, account: event.target.value } })} placeholder="Conta, assinatura ou projeto" /></label>
            </div>
          )}
          {node.type === 'erdEntity' && (
            <div className="specialized-fields entity-fields-editor">
              <div className="subsection-title">Campos da entidade</div>
              {(node.entityFields ?? []).map((field) => {
                const patchField = (patch: Partial<EntityField>) => onPatchNode({ entityFields: (node.entityFields ?? []).map((item) => item.id === field.id ? { ...item, ...patch } : item) });
                return <div className="entity-field-row" key={field.id}>
                  <input type="text" disabled={node.locked} value={field.name} aria-label="Nome do campo" onChange={(event) => patchField({ name: event.target.value })} />
                  <input type="text" disabled={node.locked} value={field.dataType} aria-label="Tipo do campo" onChange={(event) => patchField({ dataType: event.target.value })} />
                  <label title="Chave primária"><input type="checkbox" disabled={node.locked} checked={field.primaryKey} onChange={(event) => patchField({ primaryKey: event.target.checked })} />PK</label>
                  <label title="Chave estrangeira"><input type="checkbox" disabled={node.locked} checked={field.foreignKey} onChange={(event) => patchField({ foreignKey: event.target.checked })} />FK</label>
                  <button type="button" disabled={node.locked} aria-label={`Remover campo ${field.name}`} onClick={() => { const next = (node.entityFields ?? []).filter((item) => item.id !== field.id); onPatchNode({ entityFields: next, height: Math.max(110, 58 + next.length * 22) }); }}>×</button>
                </div>;
              })}
              <button type="button" className="inline-action" disabled={node.locked} onClick={() => { const next = [...(node.entityFields ?? []), { id: crypto.randomUUID(), name: 'novo_campo', dataType: 'TEXT', primaryKey: false, foreignKey: false, nullable: true }]; onPatchNode({ entityFields: next, height: Math.max(node.height, 58 + next.length * 22) }); }}>+ Adicionar campo</button>
            </div>
          )}
          <StyleFields style={node.style} disabled={node.locked} onPatch={(patch) => onPatchNode({ style: { ...node.style, ...patch } })} />
          {(node.type === 'orgPerson' || node.type === 'orgDepartment') && (
            <div className="info-card org-actions-card">
              <strong>Organograma</strong>
              <p>Crie subordinados conectados automaticamente e reorganize a hierarquia usando processamento local.</p>
              <div className="context-actions">
                <button type="button" disabled={node.locked} onClick={() => onAddOrgChild(node.id)}>+ Subordinado</button>
                <button type="button" onClick={onArrangeOrg}>Organizar hierarquia</button>
              </div>
            </div>
          )}
          {(node.type === 'mindRoot' || node.type === 'mindTopic') && (
            <div className="info-card mind-actions-card">
              <strong>Mapa mental</strong>
              <p>Crie novos ramos e distribua automaticamente os tópicos dos dois lados do tema central.</p>
              <div className="context-actions">
                <button type="button" disabled={node.locked} onClick={() => onAddMindChild(node.id)}>+ Subtópico</button>
                <button type="button" onClick={onArrangeMind}>Organizar mapa</button>
              </div>
            </div>
          )}
          <div className="info-card">
            <strong>{node.locked ? 'Objeto travado' : node.groupId ? 'Objeto agrupado' : 'Objeto independente'}</strong>
            <p>{node.locked ? 'Destrave pela barra superior para editar ou mover.' : node.groupId ? 'Selecionar este objeto seleciona todo o grupo.' : 'Pode ser agrupado com outros objetos usando Ctrl+G.'}</p>
          </div>
          <DataFields node={node} onPatchNode={onPatchNode} />
        </div>
      ) : null}
    </aside>
  );
}
